import { makeAspectCrop } from 'react-image-crop';
import { HeaderPhotoSchemaByWebsiteLayout, WebsitePhotoBaseProperties, WebsitePhotoCropInfo } from './useWebsiteHeaderSettings.types';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';

/**
 * Dog photo
 */
const SAMPLE_IMAGE_LANDSCAPE_1: WebsitePhotoBaseProperties = {
  id: 'b1182a3d-d630-53b0-84e0-bbd9ac1880e7',
  height: 3888,
  width: 5184,
  url: 'https://dev.withjoy.com/media/e5404ea7835e0171c4ad00450bd9a4220261a48b7887841f6/26e00890-2bca-11f0-a59b-c14b61c34094-Unsplash_jamie452_UtrE5DcgEyg.jpg'
};

/**
 * Batman - landscape
 */
const SAMPLE_IMAGE_LANDSCAPE_2: WebsitePhotoBaseProperties = {
  id: 'b1182a3d-d630-53b0-84e0-bbd9ac1880e7',
  height: 3260,
  width: 5060,
  url: 'https://dev.withjoy.com/media/e5404ea7835e0171c4ad00450bd9a4220261a48b7887841f6/9fb19390-4648-11f0-a7e3-9bab8e4a143a-Unsplash_michael_marais_FYzHFQOag8A.jpg'
};

/**
 * Labubu - portrait
 */
const SAMPLE_IMAGE_PORTRAIT_1: WebsitePhotoBaseProperties = {
  id: 'b1182a3d-d630-53b0-84e0-bbd9ac1880e7',
  height: 6240,
  width: 4160,
  url: 'https://dev.withjoy.com/media/e5404ea7835e0171c4ad00450bd9a4220261a48b7887841f6/f1299830-4648-11f0-a7e3-9bab8e4a143a-Unsplash_davidkristianto_B0istPfk3Go.jpg'
};

/**
 * Cat - portrait
 */
const SAMPLE_IMAGE_PORTRAIT_2: WebsitePhotoBaseProperties = {
  id: 'b1182a3d-d630-53b0-84e0-bbd9ac1880e7',
  height: 5866,
  width: 3035,
  url: 'https://dev.withjoy.com/media/e5404ea7835e0171c4ad00450bd9a4220261a48b7887841f6/ae42b690-4649-11f0-a192-2d2f7e1aff59-Unsplash_niuhang_Tn8DLxwuDMA.jpg'
};

const deriveCropInfo = (image: WebsitePhotoBaseProperties, cropArgs: { width: number; y: number }, aspectRatio: number): WebsitePhotoCropInfo => {
  return {
    ...makeAspectCrop(
      {
        unit: '%',
        width: cropArgs.width,
        y: cropArgs.y
      },
      aspectRatio,
      image.width,
      image.height
    ),
    unit: 'percent'
  };
};

export const MOCK_HEADER_SETTINGS: HeaderPhotoSchemaByWebsiteLayout = {
  aloha: {
    center: {
      $id: 'https://withjoy.com/assets/public/websiteHeaderSchemas/center-v1_1_1.json',
      presentationLayout: 'center',
      photo: {
        ...SAMPLE_IMAGE_LANDSCAPE_1,
        alignment: {
          alignX: 'right',
          alignY: 'center'
        }
      }
    },
    photoSlider: {
      $id: 'https://withjoy.com/assets/public/websiteHeaderSchemas/photoSlider-v1_1_1.json',
      presentationLayout: 'photoSlider',
      photos: [
        {
          ...SAMPLE_IMAGE_LANDSCAPE_1,
          cropInfo: deriveCropInfo(
            SAMPLE_IMAGE_LANDSCAPE_1,
            {
              width: 100,
              y: 30
            },
            PHOTO_PRESENTATION_CONFIG.photoSlider.aspectRatio as number
          )
        },
        {
          ...SAMPLE_IMAGE_PORTRAIT_1,
          cropInfo: deriveCropInfo(
            SAMPLE_IMAGE_PORTRAIT_1,
            {
              width: 100,
              y: 30
            },
            PHOTO_PRESENTATION_CONFIG.photoSlider.aspectRatio as number
          )
        }
      ]
    }
  },
  brannan: {
    center: {
      $id: 'https://withjoy.com/assets/public/websiteHeaderSchemas/center-v1_1_1.json',
      presentationLayout: 'center',
      photo: {
        ...SAMPLE_IMAGE_LANDSCAPE_1,
        alignment: {
          alignX: 'right',
          alignY: 'center'
        }
      }
    },
    fullWidth: {
      $id: 'https://withjoy.com/assets/public/websiteHeaderSchemas/fullWidth-v1_1_1.json',
      presentationLayout: 'fullWidth',
      photo: {
        ...SAMPLE_IMAGE_LANDSCAPE_1,
        cropInfo: deriveCropInfo(
          SAMPLE_IMAGE_LANDSCAPE_1,
          {
            width: 100,
            y: 30
          },
          PHOTO_PRESENTATION_CONFIG.fullWidth.aspectRatio as number
        )
      }
    },
    pageWidth: {
      $id: 'https://withjoy.com/assets/public/websiteHeaderSchemas/pageWidth-v1_1_1.json',
      presentationLayout: 'pageWidth',
      photo: {
        ...SAMPLE_IMAGE_LANDSCAPE_2,
        cropInfo: deriveCropInfo(
          SAMPLE_IMAGE_LANDSCAPE_2,
          {
            width: 100,
            y: 30
          },
          PHOTO_PRESENTATION_CONFIG.pageWidth.aspectRatio as number
        )
      }
    },
    photoSlider: {
      $id: 'https://withjoy.com/assets/public/websiteHeaderSchemas/photoSlider-v1_1_1.json',
      presentationLayout: 'photoSlider',
      photos: [
        {
          ...SAMPLE_IMAGE_LANDSCAPE_1,
          cropInfo: deriveCropInfo(
            SAMPLE_IMAGE_LANDSCAPE_1,
            {
              width: 100,
              y: 30
            },
            PHOTO_PRESENTATION_CONFIG.photoSlider.aspectRatio as number
          )
        },
        {
          ...SAMPLE_IMAGE_PORTRAIT_1,
          cropInfo: deriveCropInfo(
            SAMPLE_IMAGE_PORTRAIT_1,
            {
              width: 100,
              y: 30
            },
            PHOTO_PRESENTATION_CONFIG.photoSlider.aspectRatio as number
          )
        }
      ]
    },
    photoTrio: {
      $id: 'https://withjoy.com/assets/public/websiteHeaderSchemas/photoTrio-v1_1_1.json',
      presentationLayout: 'photoTrio',
      photos: [
        {
          ...SAMPLE_IMAGE_LANDSCAPE_1,
          cropInfo: deriveCropInfo(
            SAMPLE_IMAGE_LANDSCAPE_1,
            {
              width: 100,
              y: 20
            },
            PHOTO_PRESENTATION_CONFIG.photoTrio.aspectRatio as number
          )
        },
        {
          ...SAMPLE_IMAGE_PORTRAIT_1,
          cropInfo: deriveCropInfo(
            SAMPLE_IMAGE_PORTRAIT_1,
            {
              width: 100,
              y: 10
            },
            PHOTO_PRESENTATION_CONFIG.photoTrio.aspectRatio as number
          )
        },
        {
          ...SAMPLE_IMAGE_PORTRAIT_2,
          cropInfo: deriveCropInfo(
            SAMPLE_IMAGE_PORTRAIT_2,
            {
              width: 100,
              y: 0
            },
            PHOTO_PRESENTATION_CONFIG.photoTrio.aspectRatio as number
          )
        }
      ]
    }
  }
};
